import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'
import { calcTableProps } from '@aytm/app/stats/page/data-centrifuge/lib/table-utils'

// components
import Controls from '@aytm/app/stats/page/data-centrifuge/Controls'
import Chart from '@aytm/app/stats/page/data-centrifuge/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'

const Block = ({ tier, reviewEvidence, job, model, features, onFetch, onChange, onFilter, onCallback }) => {
  // `calcTableProps` walks scoresFiltered + excludedScores twice with nested reduces;
  // on large surveys that is O(n·k) work. Memoise so it only re-runs when the inputs
  // it actually reads change (filter bucket + both score arrays + the suspicion-band map).
  const tableProps = useMemo(
    () => (can(tier, CAPABILITY.RELEASE_QUARANTINE) ? calcTableProps({ ...model }) : {}),
    [
      tier,
      model.filter,
      model.scores,
      model.scoresFiltered,
      model.excludedScores,
      model.advisoryFiltered,
      model.suspicionScores,
      model.dcAnalyzedCount,
      model.notAnalyzedCount
    ]
  )

  return (
    <>
      {/* The run/filter controls are an admin-tier capability — visible only for tier 0. */}
      {can(tier, CAPABILITY.RELEASE_QUARANTINE) ? (
        <Controls
          tier={tier}
          job={job}
          model={model}
          features={features}
          onFetch={onFetch}
          onChange={onChange}
          onFilter={onFilter}
        />
      ) : null}
      <Chart tier={tier} reviewEvidence={reviewEvidence} job={job} {...model} onCallback={onCallback} />
      {/* The response table is an admin-tier capability — visible only for tier 0. */}
      {can(tier, CAPABILITY.RELEASE_QUARANTINE) ? <Table {...tableProps} /> : null}
    </>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  tier: PropTypes.string,
  reviewEvidence: PropTypes.bool,
  // Data calculation props
  job: PropTypes.shape({
    status: PropTypes.string,
    vectors: PropTypes.object,
    progress: PropTypes.shape({
      current: PropTypes.number,
      real: PropTypes.number,
      vector: PropTypes.shape({
        current: PropTypes.number,
        real: PropTypes.number
      })
    })
  }),
  model: PropTypes.object,
  features: PropTypes.object,
  onFetch: PropTypes.func,
  onChange: PropTypes.func,
  onFilter: PropTypes.func,
  onCallback: PropTypes.func
}

export default Block
